import { useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { MissionDetail, MissionUpdate, Priority, Status } from '@missions/types';
import { type SelectOption, useToast } from '@missions/ui';
import { api } from '../../lib/client';
import { useApi, useErrorToast, useInvalidate } from '../../lib/hooks';
import { PRIORITY } from '../../components/atoms';

/** All members of the tenant (assignee pickers). */
export function useMemberOptions(): { options: SelectOption[]; byId: Map<string, { name: string; initials: string; color: string }> } {
  const q = useApi('members.list', { query: { limit: 100, status: 'active' } }, { staleTime: 120_000 });
  return useMemo(() => {
    const list = q.data?.data ?? [];
    return {
      options: list.map((m) => ({ value: m.id, label: m.name, avatar: m.initials, avatarColor: m.color, desc: m.email })),
      byId: new Map(list.map((m) => [m.id, { name: m.name, initials: m.initials, color: m.color }])),
    };
  }, [q.data]);
}

/** Flat project list for the current workspace (sidebar tree + pickers). */
export function useProjects(workspaceId: string) {
  const q = useApi('projects.tree', { query: { workspaceId } }, { enabled: !!workspaceId, staleTime: 60_000 });
  const flat = useMemo(() => {
    const d = q.data;
    if (!d) return [];
    return [
      ...d.folders.flatMap((f) => f.projects.map((p) => ({ ...p, folder: f.name }))),
      ...d.rootProjects.map((p) => ({ ...p, folder: null as string | null })),
    ];
  }, [q.data]);
  return { tree: q.data, flat, isPending: q.isPending };
}

export const priorityOptions = (): SelectOption<Priority>[] =>
  (Object.keys(PRIORITY) as Priority[]).map((p) => ({ value: p, label: PRIORITY[p].label, color: PRIORITY[p].color }));

export const statusOptions = (statuses: Status[]): SelectOption[] =>
  [...statuses].sort((a, b) => a.sortOrder - b.sortOrder).map((s) => ({ value: s.id, label: s.name, color: s.color }));

/** Refresh every view that shows missions after a change. */
export function useMissionInvalidate() {
  const invalidate = useInvalidate();
  return () => invalidate('missions', 'myMissions', 'missionControl', 'projects', 'timeline');
}

/** PATCH a mission; on success refreshes lists. */
export function useUpdateMission() {
  const refresh = useMissionInvalidate();
  const onErr = useErrorToast();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: MissionUpdate }) => api.call('missions.update', { params: { id }, body: patch }),
    onError: (e) => onErr(e),
    onSettled: () => refresh(),
  });
}

/** Mark complete = move to the project's first closed status, with burst + Undo (PLAN §10A). */
export function useCompleteMission() {
  const refresh = useMissionInvalidate();
  const toast = useToast();
  const onErr = useErrorToast();
  return async (m: Pick<MissionDetail, 'id' | 'title' | 'statusId'>, statuses: Status[], label: string, anchor?: HTMLElement | null) => {
    const done = statuses.find((s) => s.isClosed);
    if (!done) {
      toast('This project has no “done” status yet. Add one in Statuses.', { type: 'info' });
      return;
    }
    const prev = m.statusId;
    try {
      await api.call('missions.update', { params: { id: m.id }, body: { statusId: done.id } });
      if (anchor) celebrate(anchor);
      await refresh();
      toast(`${label}: ${m.title}`, {
        action: {
          label: 'Undo',
          onClick: () => void api.call('missions.update', { params: { id: m.id }, body: { statusId: prev } }).then(() => refresh()),
        },
      });
    } catch (e) {
      onErr(e);
    }
  };
}

/** Particle burst from an element (the checkbox that completed a mission). */
export function celebrate(el: Element): void {
  if (typeof window === 'undefined' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const r = el.getBoundingClientRect();
  const x = r.left + r.width / 2;
  const y = r.top + r.height / 2;
  const colors = ['var(--brand)', '#22c55e', '#f59e0b', '#ec4899', 'var(--brand-200)'];
  for (let i = 0; i < 14; i++) {
    const a = (Math.PI * 2 * i) / 14 + Math.random() * 0.4;
    const d = 26 + Math.random() * 26;
    const p = document.createElement('span');
    p.className = 'ms-burst';
    p.style.cssText = `left:${x}px;top:${y}px;background:${colors[i % colors.length]};--dx:${Math.cos(a) * d}px;--dy:${Math.sin(a) * d}px;--r:${Math.random() * 360}deg`;
    document.body.append(p);
    setTimeout(() => p.remove(), 800);
  }
}

/** "Project complete — All missions accomplished" banner. */
export function projectCompleteBanner(title: string, label: string, sub: string): void {
  const b = document.createElement('div');
  b.className = 'ms-accomplished';
  b.setAttribute('role', 'status');
  b.innerHTML = `<div class="flag"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 21V4"/><path d="M5 4h12l-2.5 4L17 12H5"/></svg></div><div style="min-width:0"><small></small><b></b><span style="font-size:13px;color:#c7d6ff"></span></div>`;
  b.querySelector('small')!.textContent = label;
  b.querySelector('b')!.textContent = title;
  b.querySelector('span')!.textContent = sub;
  document.body.append(b);
  requestAnimationFrame(() => requestAnimationFrame(() => b.classList.add('in')));
  celebrate(b);
  setTimeout(() => {
    b.classList.remove('in');
    setTimeout(() => b.remove(), 400);
  }, 3800);
}
